import type { AuthResponse, DepositAddressResponse, DepositHistoryResponse, FiatPriceResponse, FlexTransferRequest, FlexTransferResponse, MarketListResponse, SpotAccountListResponse, SupportedChain, TransferQuotasResponse, UserAssetList, WithdrawalQuotasResponse, WithdrawalRequest, WithdrawalResponse } from "@/types/funds.types";
import { authApi } from "./api.service";

// ================= Currencies ================= //

export const getAllCurrenciesList = async (): Promise<string[]> => {
  const response = await authApi.get<string[]>('/v0/kucoin/exchange/currencies/list');
  return response.data;
};

// ================= Supported Chains ================= //

export const getSupportedChains = async (): Promise<SupportedChain[]> => {
  const response = await authApi.get<SupportedChain[]>('/v0/kucoin/exchange/chains/supported');
  return response.data;
};

// ================= Market List ================= //

export const getMarketList = async (): Promise<MarketListResponse> => {
  const response = await authApi.get<MarketListResponse>('/v0/kucoin/exchange/market-list');
  return response.data;
};

// ================= Deposit Addresses ================= //

export const addDepositAddressV3 = async (currency: string, chain?: string): Promise<DepositAddressResponse> => {
  const response = await authApi.post<DepositAddressResponse>('/v0/kucoin/user/funds/deposit-address', {
    currency,
    chain: chain || ''
  });
  return response.data;
};

export const getDepositAddressV3 = async (currency: string, chain?: string): Promise<DepositAddressResponse> => {
  const params = new URLSearchParams();
  params.append('currency', currency);
  if (chain) {
    params.append('chain', chain);
  }

  const response = await authApi.get<DepositAddressResponse>(`/v0/kucoin/user/funds/deposit-address?${params.toString()}`);
  return response.data;
};

export const getOrCreateDepositAddress = async (currency: string, chain?: string): Promise<DepositAddressResponse> => {
  const response = await authApi.post<{
    success: boolean;
    timestamp: number;
    data: DepositAddressResponse;
  }>('/v0/kucoin/user/funds/deposit-address/get-or-create', {
    currency,
    chain: chain || ''
  });
  return response.data.data;
};

// ================= Deposit History ================= //

export const getDepositHistory = async (
  currency?: string,
  startAt?: number,
  endAt?: number,
  status?: string,
  currentPage?: number,
  pageSize?: number
): Promise<DepositHistoryResponse> => {
  const params = new URLSearchParams();

  if (currency) params.append('currency', currency);
  if (startAt) params.append('startAt', startAt.toString());
  if (endAt) params.append('endAt', endAt.toString());
  if (status) params.append('status', status);
  if (currentPage) params.append('currentPage', currentPage.toString());
  if (pageSize) params.append('pageSize', pageSize.toString());

  const url = `/v0/kucoin/user/funds/deposit-history${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await authApi.get<DepositHistoryResponse>(url);
  return response.data;
};

// ================= Withdrawal Operations ================= //

export const getWithdrawalQuotas = async (currency: string): Promise<WithdrawalQuotasResponse> => {
  const response = await authApi.get<WithdrawalQuotasResponse>(`/v0/kucoin/user/funds/withdrawal-quotas?currency=${currency}`);
  return response.data;
};

export const withdrawalV3 = async (withdrawalData: WithdrawalRequest): Promise<WithdrawalResponse> => {
  const response = await authApi.post<WithdrawalResponse>('/v0/kucoin/user/funds/withdrawal', withdrawalData);
  return response.data;
};

export const cancelWithdrawal = async (withdrawalId: string): Promise<{ success: boolean; message?: string; }> => {
  const response = await authApi.post<{ success: boolean; message?: string; }>('/v0/kucoin/user/funds/cancel-withdrawal', {
    withdrawalId
  });
  return response.data;
};

export const getWithdrawalHistory = async (
  currency?: string,
  startAt?: number,
  endAt?: number,
  status?: string,
  currentPage?: number,
  pageSize?: number
): Promise<DepositHistoryResponse> => {
  const params = new URLSearchParams();

  if (currency) params.append('currency', currency);
  if (startAt) params.append('startAt', startAt.toString());
  if (endAt) params.append('endAt', endAt.toString());
  if (status) params.append('status', status);
  if (currentPage) params.append('currentPage', currentPage.toString());
  if (pageSize) params.append('pageSize', pageSize.toString());

  const url = `/v0/kucoin/user/funds/withdrawal-history${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await authApi.get<DepositHistoryResponse>(url);
  return response.data;
};

export const getWithdrawalHistoryById = async (withdrawalId: string): Promise<any> => {
  const response = await authApi.get<any>(`/v0/kucoin/user/funds/withdrawal-history/${withdrawalId}`);
  return response.data;
};

// ================= Transfer Operations ================= //

export const getTransferQuotas = async (currency: string): Promise<TransferQuotasResponse> => {
  const response = await authApi.get<TransferQuotasResponse>(`/v0/kucoin/user/funds/transfer-quotas?currency=${currency}`);
  return response.data;
};

export const flexTransfer = async (transferData: FlexTransferRequest): Promise<FlexTransferResponse> => {
  const response = await authApi.post<FlexTransferResponse>('/v0/kucoin/user/funds/flex-transfer', transferData);
  return response.data;
};

// ================= Fiat Price ================= //

export const getFiatPrice = async (
  currencies?: string,
  base?: string
): Promise<FiatPriceResponse> => {
  const params = new URLSearchParams();

  if (currencies) {
    params.append('currencies', currencies);
  }

  if (base) {
    params.append('base', base);
  }

  const queryString = params.toString();
  const url = `/v0/kucoin/exchange/fiat-price${queryString ? `?${queryString}` : ''}`;

  const response = await authApi.get<FiatPriceResponse>(url);
  return response.data;
};

export const getUserAssetsList = async () => {
  const url = `/v0/kucoin/user/funds/user-assets-list`;
  const response = await authApi.get<AuthResponse<UserAssetList>>(url);
  return response.data;
};

export const getSpotAccountList = async (currency: string) => {
  const params = new URLSearchParams();
  params.append('currency', currency);

  // if (currency === 'USDT') {
  //   return {
  //     data: {
  //       "data": [
  //         {
  //           "id": "331693364892676",
  //           "currency": "USDT",
  //           "type": "trade",
  //           "balance": "100.02",
  //           "available": "100.02",
  //           "holds": "0"
  //         }
  //       ]
  //     }
  //   };
  // }
  // if (currency === 'ETH') {
  //   return {
  //     data: {
  //       "data": [
  //         {
  //           "id": "331693364892675",
  //           "currency": "ETH",
  //           "type": "trade",
  //           "balance": "1.001",
  //           "available": "1.001",
  //           "holds": "0"
  //         }
  //       ]
  //     }
  //   };
  // }
  // if (currency === 'BTC') {
  //   return {
  //     data: {
  //       "data": [
  //         {
  //           "id": "331693364892675",
  //           "currency": "BTC",
  //           "type": "trade",
  //           "balance": "0.001",
  //           "available": "0.001",
  //           "holds": "0"
  //         }
  //       ]
  //     }
  //   };
  // }

  const response = await authApi.get<AuthResponse<SpotAccountListResponse>>(`/v0/kucoin/user/funds/spot-account-list?${params.toString()}`);
  return response.data;
};
